import * as React from 'react';
import { useTranslation } from 'next-i18next';
import { useAtomValue } from 'jotai';

import { ratingCoordinateProps, ratingIconProps } from '@restworld/utils-common';
import { useResponsiveBreakPoints } from '@restworld/utils-common';
import { HandThumbUpIcon } from '@heroicons/react/24/outline';
import { Heading } from '@restworld/ui-ds-base';
import { Wrapper } from '@restworld/ui-components';
import { ListTertiary } from '../../legacy/list-tertiary';

import { buildRecruiterInsightItem } from '../../../lib/job-position/recruiter-insight';
import { JobApplicationEntryPoint } from '../job-application-entry-point';
import { jobPositionApiDataAtom } from '../job-position.atoms';

export const PerformanceBlock = () => {
  const { jobPerformanceItemList } = usePerformanceBlock();
  return jobPerformanceItemList.length ? (
    <>
      <JobPerformanceBlock jobPerformanceItemList={jobPerformanceItemList} />
      <hr className="text-gray-300" />
    </>
  ) : null;
};
interface IJobPerformanceBlockProps {
  jobPerformanceItemList: tertiaryItemListProps[];
  customerServiceHelper?: string;
  // careerPossibilityDescription?: string;
}
export const JobPerformanceBlock: React.FunctionComponent<IJobPerformanceBlockProps> = ({ jobPerformanceItemList }) => {
  const { t: translateCommon } = useTranslation('common');
  const { isDesktop } = useResponsiveBreakPoints();
  return (
    <Wrapper>
      <span className="flex flex-row items-center gap-1.5">
        <HandThumbUpIcon className="h-5" />
        <Heading>{translateCommon('our_advice')}</Heading>
      </span>
      <ListTertiary jobPerformanceItemList={jobPerformanceItemList} />
      {!isDesktop && (
        <>
          <JobApplicationEntryPoint />
        </>
      )}
    </Wrapper>
  );
};
const usePerformanceBlock = () => {
  const { t: commonTranslate } = useTranslation('common');
  const { t: jobPositionTranslate } = useTranslation('job-position-page');
  const jobPositionApiData = useAtomValue(jobPositionApiDataAtom);

  const customerServiceHelper = React.useMemo(
    () => jobPositionApiData?.assigned_to_user?.name,
    [jobPositionApiData?.assigned_to_user?.name]
  );
  const recruiterInsightItem = React.useMemo(
    () => buildRecruiterInsightItem(jobPositionApiData, commonTranslate),
    [jobPositionApiData, commonTranslate]
  );
  const formatSalaryRating = React.useCallback(
    (rating: 1 | 2 | 3 | 4 | 5) => {
      switch (rating) {
        case 1:
          return jobPositionTranslate('job_low_pay');
        case 2:
          return jobPositionTranslate('job_below_average_pay');
        case 3:
          return jobPositionTranslate('job_average_pay');
        case 4:
          return jobPositionTranslate('job_above_average_pay');
        case 5:
          return jobPositionTranslate('job_high_average_pay');
        default:
          return '';
      }
    },
    [jobPositionTranslate]
  );

  const formatCareerRating = React.useCallback(
    (rating: 1 | 2 | 3 | 4 | 5) => {
      switch (rating) {
        case 1:
          return jobPositionTranslate('job_gives_low_career_dev');
        case 2:
          return jobPositionTranslate('job_gives_below_average_career_dev');
        case 3:
          return jobPositionTranslate('job_gives_average_career_dev');
        case 4:
          return jobPositionTranslate('job_gives_above_average_career_dev');
        case 5:
          return jobPositionTranslate('job_gives_high_career_dev');
        default:
          return '';
      }
    },
    [jobPositionTranslate]
  );

  const formatWorklifeBalanceRating = React.useCallback(
    (rating: 1 | 2 | 3 | 4 | 5) => {
      switch (rating) {
        case 1:
          return jobPositionTranslate('job_gives_low_worklife_balance');
        case 2:
          return jobPositionTranslate('job_gives_below_average_worklife_balance');
        case 3:
          return jobPositionTranslate('job_gives_average_worklife_balance');
        case 4:
          return jobPositionTranslate('job_gives_above_average_worklife_balance');
        case 5:
          return jobPositionTranslate('job_gives_high_worklife_balance');
        default:
          return '';
      }
    },
    [jobPositionTranslate]
  );

  const jobPerformanceItemList: tertiaryItemListProps[] = React.useMemo(
    () => [
      ...(recruiterInsightItem ? [recruiterInsightItem] : []),
      ...(jobPositionApiData?.salary_rating
        ? [
            {
              key: 'pay',
              icon: 'rating-currency-icon',
              rating: jobPositionApiData?.salary_rating as ratingCoordinateProps,
              title: commonTranslate('salary'),
              subTitle: formatSalaryRating(jobPositionApiData.salary_rating),
            },
          ]
        : []),
      ...(jobPositionApiData?.career_rating
        ? [
            {
              key: 'career-possibilities',
              icon: 'rating-graph-icon',
              rating: jobPositionApiData?.career_rating as ratingCoordinateProps,
              title: jobPositionTranslate('job_career_possibilities.title'),
              subTitle:
                jobPositionApiData?.career_development_description ||
                formatCareerRating(jobPositionApiData.career_rating),
              hasChild: true,
            },
          ]
        : []),
      ...(jobPositionApiData?.worklife_balance_rating
        ? [
            {
              key: 'worklife-balance',
              icon: 'rating-clock-icon',
              rating: jobPositionApiData?.worklife_balance_rating as ratingCoordinateProps,
              title: jobPositionTranslate('job_worklife_balance_title'),
              subTitle: formatWorklifeBalanceRating(jobPositionApiData.worklife_balance_rating),
            },
          ]
        : []),
    ],
    [
      commonTranslate,
      formatCareerRating,
      formatSalaryRating,
      formatWorklifeBalanceRating,
      jobPositionApiData?.career_development_description,
      jobPositionApiData?.career_rating,
      jobPositionApiData?.salary_rating,
      jobPositionApiData?.worklife_balance_rating,
      jobPositionTranslate,
      recruiterInsightItem,
    ]
  );
  return {
    jobPerformanceItemList,
    customerServiceHelper,
    // careerPossibilityDescription: jobPositionApiData.career_development_description,
  };
};
interface tertiaryItemListProps {
  key: string;
  subTitle?: string;
  title: string;
  hasChild?: boolean;
  icon: ratingIconProps | string;
  rating: ratingCoordinateProps;
}
